import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, SortEnum } from '@sport-activities/sdk';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { AttendeeCityAutocompleteComponent } from '~/attendee/components/shared/city-autocomplete/city-autocomplete.component';
import {
  AttendeeDateFilterComponent,
  DateRangeFilterInterface,
} from '~/attendee/components/shared/date-filter/date-filter.component';
import { JumbotronVariantEnum } from '~/attendee/components/shared/jumbotron/enums/jumbotron-variant.enum';
import { PaginationTypeEnum } from '~/attendee/components/shared/pagination/enums/pagination-type.enum';
import { AttendeeSportFilterComponent } from '~/attendee/components/shared/sport-filter/sport-filter.component';
import { ActivityFilterParamsInterface } from '~/attendee/interfaces/activity-filter-params.interface';
import { SportGroup } from '~/attendee/interfaces/sport-group.interface';
import { NavigatorService } from '~/attendee/service/navigator.service';
import { PaginatedSearchService } from '~/attendee/service/paginated-search.service';
import {
  ResultPageFilters,
  ResultPageFilterService,
} from '~/attendee/service/result-page-filter.service';
import { SubscriptionSearchService } from '~/attendee/service/subscription-search.service';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { PageStyleEnum } from '~/cms/enums/page-style.enum';
import { buildRangeQuery } from '~/cms/helpers/build-range-query.helper';
import { OfferPageCms } from '~/cms/interfaces/offer-page.cms.interface';
import { CmsService } from '~/cms/services/cms/cms.service';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { deepMerge } from '~/core/helpers/object/deep-merge/deep-merge.helper';
import { Activity } from '~/core/model/activity.model';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { MapsService } from '~/core/services/maps/maps.service';
import { OpenGraphService } from '~/core/services/open-graph/open-graph.service';
import { TitleService } from '~/core/services/title/title.service';
import { WindowService } from '~/core/services/window/window.service';

const OFFER_LOADER_KEY = 'loadOffers';

@Component({
  selector: 'app-offer-page',
  templateUrl: './offer-page.component.html',
  styleUrls: ['./offer-page.component.scss'],
})
export class OfferPageComponent implements OnInit, OnDestroy {
  @ViewChild('dateFilter') public dateFilter: AttendeeDateFilterComponent;
  @ViewChild('sportFilter') public sportFilter: AttendeeSportFilterComponent;
  @ViewChild('autocomplete')
  public autocomplete: AttendeeCityAutocompleteComponent;

  private readonly subscriptions = new Subscription();
  public hasLoadedActivities = new BehaviorSubject<boolean>(false);

  public readonly paginationType = PaginationTypeEnum;

  /** UI related */
  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;

  private paramFilters: ResultPageFilters;
  private place: { city: string; placeId: string };

  public cmsPage: OfferPageCms;
  public defaultDateFilterRange: number;
  public defaultDateFilterValue;
  public defaultPlaceValue: string;
  public defaultRadiusValue: number;
  public defaultSportGroupValue: number[];
  public hasLoadMoreButton: boolean;
  public isLoading = false;
  public items: ActivityInterface[] | Event[] = []; // for activity view
  public totalItems: number;
  public pagination: number;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cms: CmsService,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly titleService: TitleService,
    private readonly openGraphService: OpenGraphService,
    private readonly mapsService: MapsService,
    @Inject('ActivitySearchService')
    private readonly searchService: PaginatedSearchService<
      Activity,
      ActivityFilterParamsInterface
    >,
    private readonly subscriptionSearchService: SubscriptionSearchService,
    private readonly loaderService: LoaderService,
    private readonly windowService: WindowService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly navigatorService: NavigatorService,
    private readonly resultPageFilterService: ResultPageFilterService,
    private readonly router: Router,
    private readonly customHeadTag: CustomHeadTagService,
    private readonly viewportScroller: ViewportScroller,
    private readonly translateService: TranslateService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.openGraphService.removeMeta();
    this.customHeadTag.destroyMicrodata();
  }

  public ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.navigatorService.buildUrlTree();
      this.mapsService.initMapScript();
    }

    this.mapsService.mapScriptInitialized().subscribe((woosmapLoaded) => {
      if (woosmapLoaded === true) {
        this.initOfferPage();
      }
    });
  }

  public initOfferPage(): void {
    this.subscriptions.add(
      this.route.queryParams.subscribe(
        ({ city, radius, placeId, sportGroups, startDate, endDate, page }) => {
          this.defaultSportGroupValue = sportGroups
            ?.split(',')
            .map((sportGroupId) => +sportGroupId);

          if (startDate) {
            this.defaultDateFilterValue = {
              from: new Date(startDate),
            };
          }

          if (endDate) {
            this.defaultDateFilterValue = {
              ...this.defaultDateFilterValue,
              to: new Date(endDate),
            };
          }

          this.defaultPlaceValue = city;
          this.defaultRadiusValue = radius;
          this.pagination = page;

          this.resultPageFilterService.filters$.next({
            sportGroups,
            city,
            placeId,
            radius,
            startDate,
            endDate,
            page,
          });
        }
      )
    );
    this.subscriptions.add(
      this.getResults(false, false).subscribe(
        ({ results, cmsPage, isActivitiesPageStyle, paramFilters, events }) => {
          this.loaderService.stop(OFFER_LOADER_KEY);
          this.cmsPage = cmsPage;
          this.paramFilters = paramFilters;

          this.titleService.setPageTitle(cmsPage.title);
          this.customHeadTag.setBreadcrumb([
            this.customHeadTag.newBreadcrumItem(cmsPage.title),
          ]);

          if (!isActivitiesPageStyle) {
            this.items = (this.items as Event[])?.concat(events?.eventsByDate);
          } else {
            this.items = results.items;
            if (
              this.items.length === appConfig.pagination.result ||
              this.paramFilters.page > 1
            ) {
              this.hasLoadedActivities.next(true);
            } else {
              this.totalItems = results.total;
              this.pagination = 0;
            }
          }
        }
      )
    );

    this.subscriptions.add(
      this.hasLoadedActivities.subscribe((resultLoaded) => {
        if (resultLoaded) {
          this.subscriptions.add(
            this.getResults(true, true).subscribe((response) => {
              this.loaderService.stop(OFFER_LOADER_KEY);
              this.pagination = Math.ceil(
                response.results?.items?.length / appConfig.pagination.result
              );

              this.totalItems = response.results.total;
            })
          );
        }
      })
    );

    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(OFFER_LOADER_KEY);
      })
    );
  }

  public get paginationPage(): string {
    return `/c/${this.cmsPage?.slug}`;
  }

  public get resultTranslate(): string {
    return this.items?.length > 1
      ? this.translateService.instant('attendee.result_page.activites_results')
      : this.translateService.instant('attendee.result_page.activites_result');
  }

  private defaultFilters(cmsPage: OfferPageCms): ActivityFilterParamsInterface {
    if (cmsPage.page_style === PageStyleEnum.ACTIVITIES) {
      return this.subscriptionSearchService.getOfferPageActivitiesFilters(
        cmsPage.akeneo_group_id,
        buildRangeQuery(cmsPage.price_range)
      );
    }

    return this.subscriptionSearchService.getOfferPageCalendarFilters(
      cmsPage.akeneo_group_id,
      buildRangeQuery(cmsPage.price_range)
    );
  }

  public get currentUrl(): string {
    return this.router.url;
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute('attendee.homepage')
        .toString(),
    };
  }

  public get jumbotronVariant(): JumbotronVariantEnum {
    if (this.cmsPage?.jumbotron?.isFullWidth) {
      return JumbotronVariantEnum.FullWidthImage;
    }

    return JumbotronVariantEnum.Default;
  }

  public loadMore(): void {
    this.paramFilters.page += 1;
    this.resultPageFilterService.filters$.next(this.paramFilters);
  }

  public filterByDate(): void {
    this.windowService.nativeWindow.scroll({
      top: 0,
    });
  }

  public handleAutocompleteClear(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        latitude: null,
        longitude: null,
        distance: null,
        city: null,
        placeId: null,
        radius: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  public resetFilter(): void {
    this.sportFilter.selectedSportGroup = null;
    this.autocomplete?.clearPlaceInput();
    this.dateFilter?.clear();
    this.viewportScroller.scrollToPosition([0, 0]);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  public selectPlace(place: { city: string; placeId: string }): void {
    this.place = place;
  }

  public selectRadius(radius: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        city: this.place.city,
        placeId: this.place.placeId,
        radius,
      },
      queryParamsHandling: 'merge',
    });
  }

  public setSelectedDateRange(dateRange: DateRangeFilterInterface): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        startDate: dateRange?.from?.toISOString() || null,
        endDate: dateRange?.to?.toISOString() || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  public setSelectedSportGroup(sportGroup: SportGroup): void {
    const sportGroups = sportGroup?.idCollection?.toString();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        sportGroups,
      },
      queryParamsHandling: 'merge',
    });
  }

  private getResults(
    fromZero: boolean,
    limitFixed: boolean
  ): Observable<{
    results;
    cmsPage;
    isActivitiesPageStyle;
    paramFilters;
    events;
    itemsPerPage;
  }> {
    return combineLatest([
      this.route.paramMap,
      this.resultPageFilterService.filters$,
    ]).pipe(
      mergeMap(([params, paramFilters]) => {
        this.loaderService.start(OFFER_LOADER_KEY);

        return this.cms.getOfferPageBySlug(params.get('slug')).pipe(
          tap((cmsPage) => {
            if (!cmsPage) {
              const translatedPath = this.localizeRouterService.translateRoute([
                '/attendee.not-found',
              ]) as string[];
              this.router.navigate(translatedPath);
              return EMPTY;
            }
          }),
          filter((cmsPage): cmsPage is OfferPageCms => !!cmsPage),
          map((cmsPage) => ({ cmsPage, paramFilters }))
        );
      }),
      filter(({ cmsPage }) => !!cmsPage),
      mergeMap((data) => {
        const itemsPerPage = appConfig.pagination.result;
        return of({ ...data, itemsPerPage });
      }),
      mergeMap((data) => {
        const { paramFilters } = data;
        if (data.paramFilters?.placeId) {
          return this.mapsService.getPlace(paramFilters.placeId).pipe(
            map((placeResult) => ({
              ...data,
              placeResult,
            }))
          );
        }

        return of({ ...data, placeResult: null });
      }),
      mergeMap(({ cmsPage, itemsPerPage, paramFilters, placeResult }) => {
        const defaultFilters = this.defaultFilters(cmsPage);
        const isActivitiesPageStyle =
          cmsPage.page_style === PageStyleEnum.ACTIVITIES;

        const filters = deepMerge<ActivityFilterParamsInterface>(
          defaultFilters,
          {
            sportGroups: paramFilters?.sportGroups
              ?.toString()
              .split(',')
              .map((x) => +x),
            timeslots: {
              startDate:
                paramFilters.startDate || defaultFilters.timeslots.startDate,
              endDate: paramFilters.endDate,
            },
            pagination: {
              from: fromZero ? 0 : (paramFilters?.page - 1) * itemsPerPage || 0,
              limit: limitFixed ? 2000 : itemsPerPage,
            },
            includeNextDates: true,
          }
        );

        if (placeResult) {
          filters.geo = {
            distance: paramFilters.radius,
            longitude: +placeResult.geometry.location.lng,
            latitude: +placeResult.geometry.location.lat,
          };
        }

        return this.searchService.search(filters).pipe(
          map((results) => ({
            results,
            cmsPage,
            isActivitiesPageStyle,
            filters,
            paramFilters,
            itemsPerPage,
          }))
        );
      }),
      mergeMap((data) => {
        if (data.isActivitiesPageStyle) {
          return of({ ...data, events: null });
        }

        const timeslotsFilters = deepMerge(data.filters, {
          timeslots: {
            pagination: {
              from: (data.paramFilters.page - 1) * data.itemsPerPage || 0,
              limit: limitFixed ? 2000 : data.itemsPerPage,
            },
            sort: {
              order: 'asc' as SortEnum.ASC,
              by: 'startDate',
            },
          },
        });

        return this.subscriptionSearchService
          .getItemsForCalendarView(data.results, timeslotsFilters)
          .pipe(
            map((events) => ({
              ...data,
              events,
            }))
          );
      })
    );
  }

  public get isActivitiesPage(): boolean {
    return this.cmsPage?.page_style === PageStyleEnum.ACTIVITIES;
  }

  public get itemsType(): PaginationTypeEnum {
    return this.isActivitiesPage
      ? this.paginationType.ACTIVITIES
      : this.paginationType.TIMESLOTS;
  }
}
